import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import { SigningApiService } from '../services/api';

interface SigningTokenValidation {
  valid: boolean;
  formId: string;
  signedByEmail: string;
  signedByName: string;
}

const SigningPage: React.FC = () => {
  const { formId, token } = useParams<{ formId: string; token: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tokenData, setTokenData] = useState<SigningTokenValidation | null>(null);

  const signingApiService = new SigningApiService();

  useEffect(() => {
    if (formId && token) {
      validateToken();
    } else {
      setError('Invalid signing link. Missing form ID or token.');
      setLoading(false);
    }

    // Cleanup function to clear any existing form_id from localStorage
    // This prevents conflicts with the main form flow
    return () => {
      // Only clear if we're not in the middle of a signing process
      if (!generating) {
        localStorage.removeItem('form_id');
      }
    };
  }, [formId, token, generating]);

  const validateToken = async () => {
    if (!formId || !token) return;

    setValidating(true);
    setError(null);

    try {
      const validation = await signingApiService.validateSigningToken(formId, token);
      setTokenData(validation);
    } catch (error: any) {
      console.error('Error validating token:', error);
      setError(error.response?.data?.error || 'Invalid or expired signing link.');
    } finally {
      setValidating(false);
      setLoading(false);
    }
  };

  const handleSignNow = async () => {
    if (!formId || !token) return;

    setGenerating(true);
    setError(null);

    try {
      const response = await signingApiService.generateSigningUrl(formId, token);
      
      // Store form ID in localStorage for DocuSign callback to use
      localStorage.setItem('form_id', formId);
      
      // Redirect to DocuSign
      window.location.href = response.signingUrl;
    } catch (error: any) {
      console.error('Error generating signing URL:', error);
      setError(error.response?.data?.error || 'Failed to generate signing URL. Please try again.');
      setGenerating(false);
    }
  };

  if (loading || validating) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            {loading ? 'Loading...' : 'Validating signing link...'}
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* Logo */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <img
            src={`/${import.meta.env.VITE_LOGO_PATH}`}
            alt="Logo"
            style={{ maxWidth: '400px' }}
          />
        </Box>

        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>

        <Box sx={{ textAlign: 'center' }}>
          <Button 
            variant="outlined" 
            onClick={() => navigate('/')}
          >
            Return to Home
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Logo */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <img
          src={`/${import.meta.env.VITE_LOGO_PATH}`}
          alt="Logo"
          style={{ maxWidth: '400px' }}
        />
      </Box>

      <Card elevation={3}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom align="center" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
            Ready to Sign Your Application
          </Typography>

          <Divider sx={{ my: 3 }} />

          {tokenData && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Application Details:
              </Typography>
              
              <Box sx={{ ml: 2 }}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Form ID:</strong> {tokenData.formId}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Signer Name:</strong> {tokenData.signedByName}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Signer Email:</strong> {tokenData.signedByEmail}
                </Typography>
              </Box>
            </Box>
          )}

          <Alert severity="info" sx={{ mb: 4 }}>
            <Typography variant="body2">
              <strong>Important:</strong> This signing link is for one-time use only and will expire after use. 
              You will be redirected to DocuSign to complete the signing process.
            </Typography>
          </Alert>

          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSignNow}
              disabled={generating}
              sx={{ 
                minWidth: 200, 
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 'bold'
              }}
            >
              {generating ? (
                <>
                  <CircularProgress size={24} sx={{ mr: 2 }} />
                  Generating Signing URL...
                </>
              ) : (
                'Sign Application Now'
              )}
            </Button>
          </Box>

          <Typography variant="body2" align="center" sx={{ mt: 3, color: 'text.secondary' }}>
            After signing, you will be redirected back to confirm completion.
          </Typography>
        </CardContent>
      </Card>
    </Container>
  );
};

export default SigningPage;
